import { PrimaryGeneratedColumn, Column, Index } from 'typeorm';

import { ApiProperty } from '../../codegen';
import { EMatchStatus, ELobbyStatus } from '../../enums';
import {
  IsNumber,
  IsString,
  IsEnum,
  IsOptional,
  IsDate,
  IsObject,
  Min,
  IsUUID,
  IsBoolean,
} from '../../validators';
import { BasicEntityWithoutId } from '../basic/basic.entity';

@Index(['status', 'createdAt'])
@Index(['status', 'ownerColor'])
export class MatchEntity extends BasicEntityWithoutId {
  @ApiProperty()
  @PrimaryGeneratedColumn('uuid')
  @IsUUID()
  id: string;

  @ApiProperty()
  @IsEnum(EMatchStatus)
  @Column({ type: 'enum', enum: EMatchStatus, default: EMatchStatus.WAITING })
  status: EMatchStatus;

  @ApiProperty()
  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', length: 16, nullable: true })
  ownerColor?: string;

  // Game ID (games__game.id) for multi-game support
  @ApiProperty()
  @IsNumber()
  @IsOptional()
  @Column({ type: 'integer', nullable: true })
  gameId?: number;

  // Lobby ID (matchmaking__lobbies.id), link to the created lobby
  @ApiProperty()
  @IsOptional()
  @IsString()
  @Column({ type: 'uuid', nullable: true })
  lobbyId?: string;

  @ApiProperty()
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Column({ type: 'integer', nullable: true })
  serverId?: number;

  @ApiProperty()
  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', length: 255, nullable: true })
  serverAddress?: string;

  @ApiProperty()
  @IsOptional()
  @IsNumber()
  @Column({ type: 'integer', nullable: true })
  serverPort?: number;

  @ApiProperty()
  @IsOptional()
  @IsDate()
  @Column({ type: 'timestamptz', nullable: true })
  startedAt?: Date;

  @ApiProperty()
  @IsOptional()
  @IsDate()
  @Column({ type: 'timestamptz', nullable: true })
  endedAt?: Date;

  @ApiProperty()
  @IsOptional()
  @IsObject()
  @Column({ type: 'jsonb', nullable: true, default: null })
  results?: Record<string, unknown>;

  @ApiProperty()
  @IsOptional()
  @IsString()
  @Column({ type: 'varchar', length: 100, nullable: true })
  winningTeam?: string;

  @ApiProperty()
  @IsNumber({}, { each: true })
  @IsOptional()
  @Column({ type: 'jsonb', nullable: true, default: [] })
  playerIds?: number[];

  // Stake from the lobby (in cents)
  @ApiProperty()
  @IsNumber()
  @IsOptional()
  @Min(0)
  @Column({ type: 'integer', nullable: false, default: 0 })
  stake?: number;

  // Match winner ID
  @ApiProperty()
  @IsNumber()
  @IsOptional()
  @Column({ type: 'integer', nullable: true })
  winnerId?: number;

  // Winner payout (after commission, in cents)
  @ApiProperty()
  @IsNumber()
  @IsOptional()
  @Min(0)
  @Column({ type: 'integer', nullable: false, default: 0 })
  prize?: number;

  @ApiProperty()
  @IsOptional()
  @IsBoolean()
  @Column({ type: 'boolean', default: false })
  isFreePlay?: boolean;
}

/**
 * Lobby for 1v1 stake-based matchmaking.
 * Uses direct BasicUser relations instead of gameMode.
 */
@Index(['status', 'stake'])
@Index(['minRating', 'maxRating'])
@Index(['creatorId'])
export class LobbyEntity extends BasicEntityWithoutId {
  @ApiProperty()
  @PrimaryGeneratedColumn('uuid')
  @IsUUID()
  id: string;

  @ApiProperty()
  @IsNumber()
  @Column({ type: 'integer' })
  creatorId: number;

  @ApiProperty()
  @IsOptional()
  @IsNumber()
  @Column({ type: 'integer', nullable: true })
  opponentId?: number;

  @ApiProperty()
  @IsNumber()
  @Min(0)
  @Column({ type: 'integer', default: 0 })
  stake: number;

  // Original stake (for the stake offer mechanism)
  @ApiProperty()
  @IsNumber()
  @IsOptional()
  @Column({ type: 'integer', nullable: true })
  originalStake?: number;

  @ApiProperty()
  @IsNumber()
  @Column({ type: 'integer' })
  minRating: number;

  @ApiProperty()
  @IsNumber()
  @Column({ type: 'integer' })
  maxRating: number;

  // Game ID (games__game.id) for multi-game support
  @ApiProperty()
  @IsNumber()
  @IsOptional()
  @Column({ type: 'integer', nullable: true })
  gameId?: number;

  @ApiProperty()
  @IsOptional()
  @IsUUID()
  @Column({ type: 'uuid', nullable: true })
  rematchSourceLobbyId?: string;

  @ApiProperty()
  @IsEnum(ELobbyStatus)
  @Column({
    type: 'enum',
    enum: ELobbyStatus,
    default: ELobbyStatus.SEARCHING,
  })
  status: ELobbyStatus;

  @ApiProperty()
  @IsOptional()
  @IsBoolean()
  @Column({ type: 'boolean', default: false })
  isFreePlay?: boolean;
}
